import { useCallback, useEffect, useRef, useState } from 'react';
import { PoseLandmarker, FilesetResolver, DrawingUtils } from '@mediapipe/tasks-vision';

// Right leg landmark indices
const RIGHT_LEG_INDICES = [24, 26, 28, 30, 32]; // hip, knee, ankle, heel, toe

export const usePoseDetection = (videoRef, canvasRef, onLandmarksDetected, heelRaiseState) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  
  const poseLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const drawingUtilsRef = useRef(null);

  // Initialize MediaPipe Pose Landmarker
  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      poseLandmarkerRef.current = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      setIsLoading(false);
      console.log('PoseLandmarker initialized');
    } catch (err) {
      console.error('Failed to initialize PoseLandmarker:', err);
      setError(err.message || 'Failed to initialize pose detection');
      setIsLoading(false);
    }
  }, []);

  // Draw landmarks with right leg emphasis and position lock
  const drawLandmarks = useCallback((results) => {
    const canvas = canvasRef?.current;
    const video = videoRef?.current;
    
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!drawingUtilsRef.current) {
      drawingUtilsRef.current = new DrawingUtils(ctx);
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (results.landmarks && results.landmarks.length > 0) {
      for (const landmarks of results.landmarks) {
        const { isInRaise, isCalibrated, footAngle, kneeAngle, calibrationState } = heelRaiseState || {};
        
        // Determine right leg color based on state
        let rightLegColor;
        if (!isCalibrated) {
          rightLegColor = '#eab308'; // Yellow during calibration
        } else if (isInRaise) {
          rightLegColor = '#22c55e'; // Green when raised
        } else {
          rightLegColor = '#06b6d4'; // Cyan when locked and tracking
        }

        // Draw all body parts very dimmed (almost invisible) to focus on right leg
        drawingUtilsRef.current.drawConnectors(
          landmarks,
          PoseLandmarker.POSE_CONNECTIONS,
          { color: 'rgba(60, 60, 60, 0.2)', lineWidth: 1 }
        );
        
        // Draw non-right-leg landmarks very dim
        const otherLandmarks = landmarks.filter((_, i) => !RIGHT_LEG_INDICES.includes(i));
        drawingUtilsRef.current.drawLandmarks(
          otherLandmarks,
          { color: 'rgba(80, 80, 80, 0.2)', fillColor: 'rgba(80, 80, 80, 0.2)', radius: 2 }
        );

        // Draw right leg connections BRIGHT
        const rightLegConnections = [
          [24, 26], // hip to knee
          [26, 28], // knee to ankle
          [28, 30], // ankle to heel
          [28, 32], // ankle to toe
          [30, 32]  // heel to toe
        ];

        for (const [start, end] of rightLegConnections) {
          const p1 = landmarks[start];
          const p2 = landmarks[end];
          if (p1 && p2) {
            ctx.beginPath();
            ctx.moveTo(p1.x * canvas.width, p1.y * canvas.height);
            ctx.lineTo(p2.x * canvas.width, p2.y * canvas.height);
            ctx.strokeStyle = rightLegColor;
            ctx.lineWidth = 5;
            ctx.stroke();
          }
        }

        // Draw right leg landmarks LARGE
        const rightLegLandmarks = RIGHT_LEG_INDICES.map(i => landmarks[i]).filter(Boolean);
        drawingUtilsRef.current.drawLandmarks(
          rightLegLandmarks,
          { color: rightLegColor, fillColor: rightLegColor, radius: 12 }
        );

        // Glow effect when raised
        if (isCalibrated && isInRaise) {
          ctx.save();
          ctx.shadowColor = '#22c55e';
          ctx.shadowBlur = 30;
          
          rightLegLandmarks.forEach(landmark => {
            const x = landmark.x * canvas.width;
            const y = landmark.y * canvas.height;
            ctx.beginPath();
            ctx.arc(x, y, 18, 0, 2 * Math.PI);
            ctx.fillStyle = 'rgba(34, 197, 94, 0.5)';
            ctx.fill();
          });
          ctx.restore();
        }

        // Draw LOCKED indicator when calibrated
        if (isCalibrated) {
          const rightAnkle = landmarks[28];
          if (rightAnkle) {
            ctx.save();
            ctx.font = 'bold 14px "JetBrains Mono", monospace';
            ctx.textAlign = 'center';
            const lockX = rightAnkle.x * canvas.width;
            const lockY = rightAnkle.y * canvas.height - 30;
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.lineWidth = 3;
            ctx.strokeText('🔒 LOCKED', lockX, lockY);
            ctx.fillStyle = '#06b6d4';
            ctx.fillText('🔒 LOCKED', lockX, lockY);
            ctx.restore();
          }
        }

        // Draw status text near right foot
        const rightAnkle = landmarks[28];
        const rightHeel = landmarks[30];
        if (rightAnkle && rightHeel) {
          const textX = rightAnkle.x * canvas.width;
          const textY = Math.max(rightAnkle.y, rightHeel.y) * canvas.height + 60;
          
          ctx.save();
          ctx.font = 'bold 22px "Barlow Condensed", sans-serif';
          ctx.textAlign = 'center';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
          ctx.lineWidth = 4;
          
          let statusText;
          if (!isCalibrated) {
            statusText = 'CALIBRATING...';
          } else if (isInRaise) {
            statusText = '▲ HEEL UP';
          } else {
            statusText = '▼ RAISE HEEL';
          }
          
          ctx.strokeText(statusText, textX, textY);
          ctx.fillStyle = rightLegColor;
          ctx.fillText(statusText, textX, textY);
          ctx.restore();

          // Show knee angle
          if (isCalibrated && kneeAngle) {
            const knee = landmarks[26];
            if (knee) {
              ctx.save();
              ctx.font = 'bold 16px "JetBrains Mono", monospace';
              ctx.textAlign = 'center';
              const kneeX = knee.x * canvas.width + 50;
              const kneeY = knee.y * canvas.height;
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
              ctx.lineWidth = 3;
              ctx.strokeText(`${kneeAngle}°`, kneeX, kneeY);
              ctx.fillStyle = kneeAngle >= 155 ? '#22c55e' : '#ef4444';
              ctx.fillText(`${kneeAngle}°`, kneeX, kneeY);
              ctx.restore();
            }
          }
        }
      }
    }
  }, [canvasRef, videoRef, heelRaiseState]);

  // Process video frame
  const processFrame = useCallback(() => {
    const video = videoRef?.current;
    const poseLandmarker = poseLandmarkerRef.current;

    if (!video || !poseLandmarker || video.readyState < 2) {
      animationFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      
      try {
        const startTimeMs = performance.now();
        const results = poseLandmarker.detectForVideo(video, startTimeMs);
        
        drawLandmarks(results);
        
        if (results.landmarks && results.landmarks.length > 0) {
          if (onLandmarksDetected) {
            onLandmarksDetected(results.landmarks[0]);
          }
        }
      } catch (err) {
        console.error('Error processing frame:', err);
      }
    }

    animationFrameRef.current = requestAnimationFrame(processFrame);
  }, [videoRef, drawLandmarks, onLandmarksDetected]);

  const start = useCallback(() => {
    if (!poseLandmarkerRef.current) {
      console.error('PoseLandmarker not initialized');
      return;
    }
    setIsRunning(true);
    animationFrameRef.current = requestAnimationFrame(processFrame);
  }, [processFrame]);

  const stop = useCallback(() => {
    setIsRunning(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    initialize();
    return () => {
      stop();
      if (poseLandmarkerRef.current) {
        poseLandmarkerRef.current.close();
      }
    };
  }, [initialize, stop]);

  return { isLoading, error, isRunning, start, stop };
};

export default usePoseDetection;
